import { useCallback, useRef, useState } from "react";

import { getNearestScrollNode, getWindow } from "../../../utils";

type CallbackRef<T> = (node: T | null) => void;

function getScrollTop(node: Node | null): number {
    return (
        (node as Document)?.documentElement?.scrollTop ??
        (node as HTMLElement)?.scrollTop ??
        0
    );
}

/**
 * `useVerticalScrollDirection` returns `[ref, direction]`, where:
 *   - `ref` should be attached to the element that cares about the scroll direction
 *   - `direction` will be one of `VerticalScrollDirection.UP` or `VerticalScrollDirection.DOWN`.
 */
export function useVerticalScrollDirection(): [
    CallbackRef<HTMLElement>,
    "down" | "up" | null
] {
    const scrollTop = useRef(0);

    const [direction, setDirection] = useState<"down" | "up" | null>(null);

    const ref = useCallback((node: HTMLElement | null) => {
        if (!node) return;
        const scrollNode = getNearestScrollNode(node) ?? getWindow(node);
        scrollNode?.addEventListener("scroll", (event) => {
            const nextScrollTop = getScrollTop(event.target as Node);
            const dir = nextScrollTop < scrollTop.current ? "up" : "down";
            scrollTop.current = nextScrollTop;
            setDirection(dir);
        });
    }, []);

    return [ref, direction];
}
